/**
 * Booking distribution strategies (M6 — PM-2040/2041/2043).
 *
 * DECIMAL DISCIPLINE (PM-2041): hours are 0.01h precision. ALL distribution
 * and rounding arithmetic in this module works in INTEGER HUNDREDTHS
 * internally — `Math.round(hours * 100)` at the boundary, integer math
 * inside, `/ 100` on the way out. Floats are never accumulated: a float sum
 * of daily values drifts, an integer sum of hundredths cannot.
 */

import { z } from 'zod';

/** Selectable rounding granularities in hours (PM-2041). Extensible. */
export const BOOKING_GRANULARITIES: number[] = [0.01, 0.25, 0.5, 1];

export const BookingGranularitySchema = z
  .number()
  .refine((value) => BOOKING_GRANULARITIES.includes(value), {
    message: `granularity must be one of ${BOOKING_GRANULARITIES.join(', ')}`,
  });

/**
 * Every strategy receives the same inputs (PM-2043's shared surface): the
 * hours to place, the day grid with capacity AND existing bookings, and the
 * rounding granularity. A strategy that ignores a field simply ignores it —
 * the caller never special-cases per strategy.
 */
export type DistributionInput = {
  totalHours: number;
  days: { date: string; capacityHours: number; existingHours: number }[];
  granularity: number;
};

/**
 * One interface, one registry, for BOTH distribution (M6) and redistribution
 * (M7) strategies — PM-2043's shared-surface note. The `mode` flag is the M7
 * answer decided here (DL-015): distribution strategies REPLACE the range
 * they cover (period entry overwrites hand-tuned days — DL-029 §4,
 * deliberate), while redistribution strategies (M7: proportional-add,
 * fill-free-capacity, append-to-end) are ADDITIVE on top of the existing plan
 * — they receive the same `DistributionInput` (existing bookings included)
 * and return deltas the caller adds. Like the roll-up permanence rule
 * (DL-019), the flag is declared by the strategy, never inferred by the
 * caller, so registering a new strategy never changes route code.
 */
export type DistributionStrategy = {
  key: string;
  mode: 'replace' | 'additive';
  distribute: (input: DistributionInput) => { date: string; hours: number }[];
};

export type RawHundredths = { date: string; value: number };

/** Latest-date-first tiebreak: with equal values the LATER date is chosen. */
const pickSmallest = (entries: { date: string; value: number }[], requirePositive: boolean) => {
  let best = -1;
  for (let i = 0; i < entries.length; i += 1) {
    const { value } = entries[i]!;
    if (requirePositive && value <= 0) continue;
    if (
      best === -1 ||
      value < entries[best]!.value ||
      (value === entries[best]!.value && entries[i]!.date >= entries[best]!.date)
    ) {
      best = i;
    }
  }
  return best;
};

const pickLargest = (entries: { date: string; value: number }[], atLeast: number) => {
  let best = -1;
  for (let i = 0; i < entries.length; i += 1) {
    const { value } = entries[i]!;
    if (value < atLeast) continue;
    if (
      best === -1 ||
      value > entries[best]!.value ||
      (value === entries[best]!.value && entries[i]!.date >= entries[best]!.date)
    ) {
      best = i;
    }
  }
  return best;
};

/**
 * The PM-2041 rounding-with-correction algorithm, entirely in integer
 * hundredths:
 *
 * 1. Round each raw value to the nearest granularity unit.
 * 2. diff = total − Σ rounded.
 * 3. Apply |diff| ÷ granularity whole-unit corrections one at a time:
 *    - diff > 0 → +1 unit to the SMALLEST non-zero rounded value, latest
 *      dates first on ties. Edge (documented): if every rounded value is
 *      zero, the zeros ARE the smallest values — corrections target them,
 *      latest first, so the hours are never dropped.
 *    - diff < 0 → −1 unit from the LARGEST value, latest dates first on
 *      ties. Values never go negative (only values ≥ one unit are eligible).
 * 4. When the requested total is itself not a multiple of the granularity,
 *    a sub-unit remainder (< one granularity unit) is left after step 3; it
 *    is applied once to the same target selection so that Σ === total
 *    EXACTLY — the total is sacred (never drop hours), the granularity is
 *    best-effort on that one value.
 */
export const roundWithCorrection = (
  rawHundredths: RawHundredths[],
  totalHundredths: number,
  granularityHundredths: number,
): { date: string; value: number }[] => {
  const g = granularityHundredths;
  const entries = rawHundredths.map((entry) => ({
    date: entry.date,
    value: Math.round(entry.value / g) * g,
  }));
  let diff = totalHundredths - entries.reduce((sum, entry) => sum + entry.value, 0);

  while (diff >= g) {
    let index = pickSmallest(entries, true);
    if (index === -1) index = pickSmallest(entries, false); // all-zero edge
    if (index === -1) break; // no days at all
    entries[index]!.value += g;
    diff -= g;
  }
  while (diff <= -g) {
    const index = pickLargest(entries, g);
    if (index === -1) break; // unreachable when Σ rounded > 0, kept as a guard
    entries[index]!.value -= g;
    diff += g;
  }
  // Sub-unit remainder — only when totalHundredths is not a multiple of g.
  if (diff > 0) {
    let index = pickSmallest(entries, true);
    if (index === -1) index = pickSmallest(entries, false);
    if (index !== -1) entries[index]!.value += diff;
  } else if (diff < 0) {
    const index = pickLargest(entries, -diff);
    if (index !== -1) entries[index]!.value += diff;
  }
  return entries;
};

const toHundredths = (hours: number): number => Math.round(hours * 100);

/**
 * PROPORTIONAL (PM-2040 default): raw(date) = total ÷ periodTotalCapacity ×
 * capacity(date), computed in integer hundredths. Zero-capacity dates get 0.
 * The caller guarantees Σ capacity > 0 (the zero-capacity period rule short-
 * circuits before any strategy runs).
 */
export const proportionalStrategy: DistributionStrategy = {
  key: 'PROPORTIONAL',
  mode: 'replace',
  distribute: ({ totalHours, days, granularity }) => {
    const totalH = toHundredths(totalHours);
    const g = toHundredths(granularity);
    const capacities = days.map((day) => toHundredths(day.capacityHours));
    const periodTotalCapacity = capacities.reduce((sum, value) => sum + value, 0);
    const raw = days.map((day, index) => ({
      date: day.date,
      value: periodTotalCapacity === 0 ? 0 : (totalH * capacities[index]!) / periodTotalCapacity,
    }));
    return roundWithCorrection(raw, totalH, g).map((entry) => ({
      date: entry.date,
      hours: entry.value / 100,
    }));
  },
};

/**
 * EVEN_SPLIT (the trivial second strategy PM-2043 demands): the total split
 * equally across days with capacity > 0 — across ALL days when none has
 * capacity — then rounded with correction. Non-eligible days come back as 0.
 */
export const evenSplitStrategy: DistributionStrategy = {
  key: 'EVEN_SPLIT',
  mode: 'replace',
  distribute: ({ totalHours, days, granularity }) => {
    const totalH = toHundredths(totalHours);
    const g = toHundredths(granularity);
    const eligible = days.filter((day) => toHundredths(day.capacityHours) > 0);
    const pool = eligible.length > 0 ? eligible : days;
    const rounded = roundWithCorrection(
      pool.map((day) => ({ date: day.date, value: pool.length === 0 ? 0 : totalH / pool.length })),
      totalH,
      g,
    );
    const byDate = new Map(rounded.map((entry) => [entry.date, entry.value]));
    return days.map((day) => ({ date: day.date, hours: (byDate.get(day.date) ?? 0) / 100 }));
  },
};

/**
 * PROPORTIONAL_ADD (M7 — PM-2048, DL-019): the additive mirror of
 * PROPORTIONAL. Returns per-day DELTAS proportional to capacity — the caller
 * ADDS them onto whatever bookings exist (upsert-sum), never replacing the
 * range. Existing bookings are deliberately ignored in the share computation:
 * the deviation is placed where capacity is, on top of the current plan.
 */
export const proportionalAddStrategy: DistributionStrategy = {
  key: 'PROPORTIONAL_ADD',
  mode: 'additive',
  distribute: proportionalStrategy.distribute,
};

/** EVEN_SPLIT_ADD: additive even split across days with capacity (M7, DL-019). */
export const evenSplitAddStrategy: DistributionStrategy = {
  key: 'EVEN_SPLIT_ADD',
  mode: 'additive',
  distribute: evenSplitStrategy.distribute,
};

/** The strategy registry (PM-2043) — replace (M6) and additive (M7) entries. */
export const distributionStrategies: Record<string, DistributionStrategy> = {
  [proportionalStrategy.key]: proportionalStrategy,
  [evenSplitStrategy.key]: evenSplitStrategy,
  [proportionalAddStrategy.key]: proportionalAddStrategy,
  [evenSplitAddStrategy.key]: evenSplitAddStrategy,
};

/**
 * §5.3.1 percentage entry: a percentage resolves against the resource's
 * ACTUAL period capacity — never against a flat 8h day (DL-029 §3). Integer
 * hundredths inside; the result is exact to 0.01h.
 */
export const percentageToHours = (percentage: number, periodTotalCapacity: number): number =>
  Math.round((toHundredths(periodTotalCapacity) * percentage) / 100) / 100;
